import { useEffect, useState } from "react";
import {
  fetchProjectActivities,
  fetchProjectActivityGroups,
} from "../../../api/project_activity";

/**
 * Loads activities + groups for pickers. Groups are fetched separately so
 * headers show as soon as the project has groups — not only after an
 * activity already carries group_id.
 */
export function useProjectActivityPickerData(
  projectId,
  { enabled = true, listFetchParams = null } = {}
) {
  const [activities, setActivities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !projectId) {
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const activityOpts =
      listFetchParams && Object.keys(listFetchParams).length > 0
        ? { params: listFetchParams }
        : {};

    Promise.all([
      fetchProjectActivities(projectId, activityOpts),
      fetchProjectActivityGroups(projectId).catch(() => []),
    ])
      .then(([rows, groupRows]) => {
        if (cancelled) return;
        const groupList = Array.isArray(groupRows) ? groupRows : [];
        const byId = new Map(groupList.map((g) => [String(g.id), g]));
        const list = (Array.isArray(rows) ? rows : []).map((row) => {
          const gid = row?.group_id ?? row?.groupId ?? row?.group?.id;
          if (gid == null || gid === "") return row;
          const g = byId.get(String(gid));
          if (!g) return row;
          return {
            ...row,
            group_id: gid,
            group: row.group || { id: g.id, title: g.title, row_order: g.row_order },
          };
        });
        setActivities(list);
        setGroups(groupList);
      })
      .catch(() => {
        if (cancelled) return;
        setActivities([]);
        setGroups([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, projectId, listFetchParams]);

  return { activities, groups, loading, error };
}
